import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { GlobeLock, AlertTriangle, Users, GitBranch, ArrowRight } from "lucide-react";
import G2Badge from "./G2Badge";
import { trackFreeSignupClick } from "../utils/conversions";
const HeroSection = () => {
  const { t } = useTranslation("home");
  const [cycle, setCycle] = useState(0);
  const CYCLE_DURATION = 8;

  const threatDatabase = [
    {
      darkWeb: { title: t("threats.darkWebMention"), detail: t("threats.detail1_darkweb") },
      corporate: { title: t("threats.corporateLeak"), detail: t("threats.detail1_corporate") },
      github: { title: t("threats.githubSecret"), detail: t("threats.detail1_github") },
      domain: { title: t("threats.phishingDomain"), detail: t("threats.detail1_domain") }
    },
    {
      darkWeb: { title: t("threats.darkWebMention"), detail: t("threats.detail2_darkweb") },
      corporate: { title: t("threats.stealerInfection"), detail: t("threats.detail2_corporate") },
      github: { title: t("threats.repositoryScan"), detail: t("threats.detail2_github") },
      domain: { title: t("threats.lookalikeAlert"), detail: t("threats.detail2_domain") }
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCycle((prev) => (prev + 1) % threatDatabase.length);
    }, CYCLE_DURATION * 1000);
    return () => clearInterval(interval);
  }, []);

  const currentData = threatDatabase[cycle];

  return (
    <section className="relative bg-[#020617] pt-20 pb-20 md:pt-24 md:pb-52 px-4 overflow-hidden min-h-screen flex items-center text-white font-sans">
      
      {/* Background Layer */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src="/assets/banner-background.png"
          className="w-full h-full object-cover opacity-[0.05] mix-blend-screen"
          alt="Network background"
          width={1920}
          height={1080}
        />
      </div>


      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-10 items-center">
        
        {/* 1. Headline & Description */}
        {/* Added z-20 to ensure text sits above any potential overlap */}
        <div className="lg:col-span-6 xl:col-span-7 order-1 flex flex-col items-center lg:items-start text-center lg:text-left mt-24 lg:mt-0 relative z-20">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-[28px] xs:text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-4">
              {t("hero.title")}<br className="hidden lg:block" />
              <span className="text-slate-500 font-medium block lg:inline lg:ml-2"> {t("hero.titleSuffix")}</span>
            </h1>

            

            <p className="block text-lg lg:text-lg xl:text-xl text-slate-300 max-w-2xl leading-relaxed mb-10 px-2 lg:px-0">
              {t("hero.description")}
            </p>
          </motion.div>
             <G2Badge rating={4.8} />
        </div>

        {/* 2. Radar Visual */}
        <div className="lg:col-span-6 xl:col-span-5 order-2 w-full relative z-10">
          
          {/* MOBILE FIX: 
             1. 'h-[320px]' reserves height so content doesn't collapse.
             2. 'w-full max-w-[100vw]' ensures container never exceeds screen width.
             3. 'overflow-hidden' clips any accidental spillover.
          */}
          <div className="relative w-full h-[320px] lg:h-auto flex justify-center items-center lg:block max-w-[100vw] overflow-hidden lg:overflow-visible">
              
              {/* ABSOLUTE POSITIONING FIX:
                  On mobile (default), this is 'absolute' and centered using 'left-1/2 top-1/2 -translate...'.
                  This takes it OUT of the document flow, so its 480px width does NOT stretch the page.
                  On Desktop (lg:), it reverts to 'relative' and standard positioning.
              */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:relative lg:left-auto lg:top-auto lg:translate-x-0 lg:translate-y-0 lg:flex lg:justify-center">
                
                {/* The Scaled Content */}
                <div className="relative scale-[0.55] xs:scale-[0.7] sm:scale-[0.85] lg:scale-90 xl:scale-100 transition-transform duration-500 origin-center">
                    
                    {/* Radar Circles Visual */}
                    <div className="relative flex items-center justify-center">
                      <div className="absolute z-20 pointer-events-none flex items-center justify-center">
                        
                        {/* Outer Ring */}
                        <div className="relative w-[480px] h-[480px] xl:w-[540px] xl:h-[540px] rounded-full border border-indigo-500/30 shadow-[0_0_60px_rgba(99,102,241,0.1)] overflow-hidden">
                          
                          {/* Inner Ring */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[75%] rounded-full border border-indigo-500/40 shadow-[inset_0_0_30px_rgba(99,102,241,0.15)] z-20" />
                          
                          {/* Crosshairs */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[1px] bg-indigo-500/10 z-10"></div>
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-full bg-indigo-500/10 z-10"></div>

                          {/* THE SWEEP */}
                          <motion.div
                            initial={{ rotate: 0 }}
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: CYCLE_DURATION, ease: "linear" }}
                            className="absolute inset-0 z-10 rounded-full"
                            style={{
                              background: `conic-gradient(from 0deg at 50% 50%, transparent 0deg, transparent 240deg, rgba(168, 85, 247, 0.05) 280deg, rgba(99, 102, 241, 0.4) 340deg, rgba(99, 102, 241, 0.8) 360deg)`
                            }}
                          >
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-indigo-400 rounded-full blur-[2px] shadow-[0_0_10px_white]"></div>
                          </motion.div>
                        </div>
                      </div>

                      {/* Central Dashboard UI */}
                      <div className="relative rounded-2xl border border-slate-800 overflow-hidden bg-slate-950 z-10 shadow-2xl w-[280px] md:w-full md:max-w-[400px]">
                        <div className="bg-slate-900/80 px-4 py-2 border-b border-slate-800 flex items-center gap-1.5 text-[9px] font-mono text-slate-500 uppercase">
                          <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                          <span className="ml-2 tracking-widest">{t("hero.dashboardLabel")}</span>
                        </div>
                        <img src="/assets/dashboard.png" className="w-full h-auto opacity-50" alt="UI" loading="eager" fetchpriority="high" width={1200} height={800} />
                      </div>

                      {/* Floating Finding Cards */}
                      <div className="absolute inset-0 z-30 pointer-events-none">
                        <AnimatePresence mode="wait">
                          <motion.div key={cycle} className="absolute inset-0">
                            <FindingCard pos="-top-20 -left-12" icon={<GlobeLock size={18}/>} data={currentData.darkWeb} delay={0} />
                            <FindingCard pos="top-4 -right-16" icon={<Users size={18}/>} data={currentData.corporate} delay={0.25} isCorporate />
                            <FindingCard pos="bottom-10 -left-20" icon={<GitBranch size={18}/>} data={currentData.github} delay={0.75} />
                            <FindingCard pos="-bottom-20 -right-12" icon={<AlertTriangle size={18}/>} data={currentData.domain} delay={0.5} />
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </div>
                </div>
              </div>
          </div>

          {/* MOBILE BUTTON */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full flex justify-center lg:hidden mt-0 px-4 relative z-20"
          >
            <a
              href="https://platform.whiteintel.io/register"
              onClick={trackFreeSignupClick}
              className="w-full max-w-[340px] mt-8 bg-gradient-to-r from-blue-600 to-indigo-600  hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-900/40 no-underline"
            >
              {t("hero.cta")} <ArrowRight size={18} />
            </a>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

// FindingCard Sub-component
const FindingCard = ({ pos, icon, data, delay, isCorporate = false }) => {
  const CYCLE_DURATION = 8;
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9, y: 10 }} 
      animate={{ opacity: [0, 1, 1, 0], scale: 1, y: 0 }}
      transition={{ duration: CYCLE_DURATION, times: [0, 0.1, 0.4, 0.5], delay: CYCLE_DURATION * delay }}
      className={`absolute ${pos} bg-slate-950/98 border border-indigo-500/30 p-4 rounded-xl shadow-2xl flex items-start gap-3 w-[250px] md:w-[260px] pointer-events-auto`}
    >
      <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-400">{icon}</div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-[10px] bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent font-bold uppercase tracking-wider">
            {data.title}
        </p>
        <p className="text-[12px] font-semibold text-white leading-tight mt-0.5">
          {isCorporate ? data.detail.split('|')[0] : data.detail}
        </p>
        <div className="flex items-center gap-1.5 mt-2.5">
          <span className="flex h-1.5 w-1.5 rounded-full bg-pink-500 animate-pulse" />
          <span className="text-[9px] font-mono text-pink-500/90 uppercase tracking-tighter">{data.liveAlert || "Live Alert"}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroSection;